import { askShowDiscount, buildQuoteSharePayload } from '../lib/quotePdf';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PiRejectedNotice } from '../components/PiRejectedNotice';
import { piErrorMessage } from '../lib/piActions';
import { ArrowLeft, FilePdf, Receipt, Clock, Lock } from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import { inrInWords } from '../lib/inrWords';
import { confirm } from '../components/ConfirmDialog';
import ShareMenu from '../components/ShareMenu';
import { fileUrl } from '../lib/fileUrl';
import PrevNextNav from '../components/PrevNextNav';
import SCViewOnlyBanner from '../components/SCViewOnlyBanner';
import { ScGatedButton, useSCRestriction } from '../components/ScGatedButton';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const [quotation, setQuotation] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [piBusy, setPiBusy] = useState(false);
  const [pi, setPi] = useState(null);
  // SC share-link gate — needs to be at the top level so React's hooks
  // rules are satisfied even when the Share button is conditionally rendered.
  const scShareGate = useSCRestriction({ action: 'share' });

  useEffect(() => {
    fetchQuotation();
    fetchTimeline();
    fetchPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Feb 2026 (iter85) — book-style navigation between quotations.
  const [quoteList, setQuoteList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/quotations`).then((r) => {
      if (!cancelled) setQuoteList(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const navItems = quoteList.map((q) => ({
    id: q.id,
    label: `${q.lead_name || 'Quote'} · ₹${Number(q.total || 0).toLocaleString('en-IN')}`,
  }));

  const fetchPI = async () => {
    try {
      const r = await axios.get(`${API}/proforma-invoices`, { params: { scope: 'all' } });
      const found = (r.data?.items || []).find(p => p.quotation_id === id);
      setPi(found || null);
    } catch (e) { /* ignore */ }
  };

  const handleGeneratePI = async () => {
    const ok = await confirm({
      title: 'Generate Proforma Invoice?',
      message: 'Generate a Proforma Invoice from this approved quote? It will require one additional approval step.',
      confirmText: 'Generate PI',
    });
    if (!ok) return;
    setPiBusy(true);
    try {
      const r = await axios.post(`${API}/quotations/${id}/generate-pi`);
      setPi(r.data.proforma_invoice);
      notify(`PI ${r.data.proforma_invoice.pi_number} created. Status: ${r.data.proforma_invoice.status}`);
    } catch (e) {
      notify(piErrorMessage(e, 'Failed to generate PI'));
    } finally {
      setPiBusy(false);
    }
  };

  const handleDownloadPI = () => {
    if (!pi) return;
    const token = localStorage.getItem('token') || '';
    window.open(`${API}/proforma-invoices/${pi.id}/pdf?token=${token}`, '_blank');
  };

  const fetchQuotation = async () => {
    try {
      const response = await axios.get(`${API}/quotations/${id}`);
      setQuotation(response.data);
    } catch (error) {
      console.error('Error fetching quotation:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(`${API}/quotations/${id}/approval-timeline`);
      setTimeline(response.data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const handleDownloadPDF = async () => {
    const show = await askShowDiscount();
    if (show === null) return;
    const token = localStorage.getItem('token') || '';
    window.open(`${API}/quotations/${id}/pdf?token=${token}&show_discount=${show}`, '_blank');
  };

  const buildSharePayload = () => buildQuoteSharePayload(quotation);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (!quotation) {
    return <div className="p-4 sm:p-6 lg:p-8"><p>Quotation not found</p></div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="quotation-detail-page">
      <SCViewOnlyBanner record={quotation} className="mb-4" />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(tp('/quotations'))}
            className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition"
          >
            <ArrowLeft size={24} />
          </button>
          <PrevNextNav
            items={navItems}
            currentId={id}
            onNavigate={(newId) => navigate(tp(`/quotations/${newId}`))}
            testIdPrefix="quotation"
          />
          <div>
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
              Quotation Details
            </h1>
            <p className="text-[#6D8E87]">ID: {quotation.id.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {/* Feb 2026 — gate distribution to APPROVED quotes. Draft/pending
              quotes are creator-visible but not downloadable or shareable so
              a customer can't be sent a price that isn't sanctioned yet. */}
          {(() => {
            const locked = quotation.status !== 'approved';
            const lockedTitle = quotation.status === 'pending_approval'
              ? `Awaiting approval by ${quotation.current_approval_stage || 'senior'}`
              : quotation.status === 'draft'
                ? 'Draft — submit for approval to unlock'
                : quotation.status === 'rejected'
                  ? 'Rejected — please revise and resubmit'
                  : '';
            return (
              <>
                <button
                  onClick={handleDownloadPDF}
                  disabled={locked}
                  title={locked ? lockedTitle : ''}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    locked
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#E2F3F0] text-[#00a69c] hover:bg-[#B5D9D2]'
                  }`}
                  data-testid="download-quote-pdf-btn"
                >
                  <FilePdf size={20} />
                  Download PDF
                </button>
              </>
            );
          })()}
          {quotation.status === 'approved' && !pi && (
            <ScGatedButton
              action="edit"
              record={quotation}
              onClick={handleGeneratePI}
              disabled={piBusy}
              className="flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#008f86] transition disabled:opacity-50"
              data-testid="generate-pi-btn"
            >
              <Receipt size={20} weight="bold" />
              {piBusy ? 'Generating...' : 'Generate Proforma Invoice'}
            </ScGatedButton>
          )}
          {pi && (
            <button
              onClick={handleDownloadPI}
              className="flex items-center gap-2 border-2 border-[#00a69c] text-[#00a69c] rounded-lg hover:bg-[#E2F3F0] transition px-4 py-2 flex items-center gap-2"
              data-testid="view-pi-btn"
              title={`PI ${pi.pi_number} • ${pi.status}`}
            >
              <Receipt size={20} weight="bold" />
              View PI ({pi.status === 'pending_approval' ? 'Pending' : pi.status === 'approved' ? 'Approved' : 'Rejected'})
            </button>
          )}
          {quotation.status === 'approved' ? (
            scShareGate.disabled ? (
              <ScGatedButton
                action="share"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-500"
                data-testid="share-quotation-sc-locked"
              >
                <Lock size={18} /> Share
              </ScGatedButton>
            ) : (
              <ShareMenu
                label="Share"
                testid="share-quotation-detail"
                getPayload={buildSharePayload}
              />
            )
          ) : (
            <button
              disabled
              title={
                quotation.status === 'pending_approval'
                  ? `Locked — awaiting approval by ${quotation.current_approval_stage || 'senior'}`
                  : 'Locked — quote must be approved before sharing'
              }
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed"
              data-testid="share-quotation-locked"
            >
              <Lock size={18} />
              Share (locked)
            </button>
          )}
        </div>
      </div>

      {/* Rejection banner — shown when quote was returned for revision. */}
      {quotation.status === 'draft' && quotation.rejection_reason && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-800" data-testid="quote-rejection-banner">
          <p className="font-semibold mb-1">Returned for revision</p>
          <p className="text-sm">
            {quotation.rejected_by ? `${quotation.rejected_by} (${quotation.rejected_by_role || 'approver'})` : 'Approver'} sent this back with note: <em>{quotation.rejection_reason}</em>
          </p>
          <p className="text-xs mt-1 text-rose-700">Edit the discount/terms and re-submit for approval.</p>
        </div>
      )}

      {pi?.status === 'rejected' && (
        <div className="mb-6">
          <PiRejectedNotice pi={pi} testid="quote-pi-rejected" onResubmitted={(u) => setPi(u)} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="medical-card p-6">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>
              Customer Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-[#6D8E87] mb-1">Customer Name</p>
                <p className="text-sm font-medium text-[#052A23]">{quotation.lead_name}</p>
              </div>
              <div>
                <p className="text-xs text-[#6D8E87] mb-1">Customer Code</p>
                <p className="text-sm font-medium text-[#052A23]">
                  {quotation.customer_code ? (
                    <span className="inline-block px-2 py-0.5 bg-[#E2F3F0] text-[#00a69c] rounded font-bold" data-testid="quotation-customer-code">{quotation.customer_code}</span>
                  ) : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#6D8E87] mb-1">Created By</p>
                <p className="text-sm font-medium text-[#052A23]">{quotation.created_by_name}</p>
              </div>
              <div>
                <p className="text-xs text-[#6D8E87] mb-1">Date</p>
                <p className="text-sm font-medium text-[#052A23]">{new Date(quotation.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-[#6D8E87] mb-1">Status</p>
                <span className={`status-badge ${quotation.status === 'approved' ? 'status-approved' : quotation.status === 'rejected' ? 'status-rejected' : 'status-pending'}`}>
                  {quotation.status.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          <div className="medical-card p-6">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>
              Items
            </h3>
            <table className="w-full">
              <thead className="bg-[#F4F9F7]">
                <tr>
                  <th className="text-left px-4 py-2 text-xs uppercase">Product</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">HSN</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">Qty</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">Price</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">Taxable</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">GST %</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">GST Amt</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">Transport</th>
                  <th className="text-left px-4 py-2 text-xs uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {quotation.items.map((item, index) => (
                  <tr key={index} className="border-t border-[#D5E8E4]">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-3">
                        {item.product_image && (
                          <img src={fileUrl(item.product_image)} alt="" className="w-14 h-14 object-contain rounded border border-[#D5E8E4] shrink-0" />
                        )}
                        <div>
                          <div>{item.product_name}</div>
                          {item.video_link && (
                            <a
                              href={item.video_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-[#00a69c] hover:underline inline-flex items-center gap-1 mt-1"
                              data-testid={`item-video-link-${index}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0C.488 3.45.029 5.804 0 12c.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0C23.512 20.55 23.971 18.196 24 12c-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/></svg>
                              Watch Video
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#6D8E87]">{item.hsn_code || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm">₹{item.price.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm">₹{(item.taxable_amount || item.price * item.quantity).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm">{item.tax_rate || 0}%</td>
                    <td className="px-4 py-3 text-sm">₹{(item.tax_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm">₹{(item.transport_charge || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#00a69c]">₹{item.total.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-6 pt-6 border-t-2 border-[#D5E8E4] space-y-2">
              <div className="flex justify-between">
                <span>Subtotal (Taxable Amount):</span>
                <span className="font-semibold">₹{quotation.subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span>Total GST:</span>
                <span className="font-semibold">₹{(quotation.total_tax || 0).toLocaleString('en-IN')}</span>
              </div>
              {(quotation.total_transport || 0) > 0 && (
                <div className="flex justify-between">
                  <span>Transportation ({quotation.transportation}):</span>
                  <span className="font-semibold">₹{(quotation.total_transport).toLocaleString('en-IN')}</span>
                </div>
              )}
              {quotation.discount > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Discount:</span>
                  <span className="font-semibold">-₹{quotation.discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-2xl font-bold text-[#00a69c] pt-2" style={{ fontFamily: 'Outfit' }}>
                <span>Grand Total:</span>
                <span>₹{quotation.total.toLocaleString('en-IN')}</span>
              </div>
              {/* Statutory "Amount in Words" line — matches the PDF exactly. */}
              <div
                className="mt-3 px-3 py-2 bg-[#F4F9F7] border-l-4 border-[#00a69c] rounded text-sm text-[#3B5A54] italic"
                data-testid="quotation-amount-in-words"
              >
                <span className="font-semibold not-italic">Amount in Words: </span>
                {inrInWords(quotation.total)}
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          {(quotation.transportation || quotation.payment_terms || quotation.warranty_years || quotation.installation || quotation.delivery_days) && (
            <div className="medical-card p-6" data-testid="tc-section">
              <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Terms &amp; Conditions</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {quotation.transportation && (
                  <div><p className="text-xs text-[#6D8E87]">Transportation</p><p className="text-sm font-medium text-[#052A23]">{quotation.transportation}</p></div>
                )}
                {quotation.payment_terms && (
                  <div><p className="text-xs text-[#6D8E87]">Payment Terms</p><p className="text-sm font-medium text-[#052A23]">{quotation.payment_terms}</p></div>
                )}
                {quotation.warranty_years > 0 && (
                  <div><p className="text-xs text-[#6D8E87]">Warranty</p><p className="text-sm font-medium text-[#052A23]">{quotation.warranty_years} Year{quotation.warranty_years > 1 ? 's' : ''}</p></div>
                )}
                {quotation.installation && (
                  <div><p className="text-xs text-[#6D8E87]">Installation</p><p className="text-sm font-medium text-[#052A23]">{quotation.installation}</p></div>
                )}
                {quotation.delivery_days > 0 && (
                  <div><p className="text-xs text-[#6D8E87]">Delivery Period</p><p className="text-sm font-medium text-[#052A23]">{quotation.delivery_days} Days</p></div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="medical-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={24} className="text-[#00a69c]" />
              <h3 className="text-lg font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
                Approval Timeline
              </h3>
            </div>

            {timeline && timeline.timeline.length > 0 ? (
              <div className="space-y-4">
                {timeline.timeline.map((stage, index) => (
                  <div key={index} className="relative pl-6">
                    {index < timeline.timeline.length - 1 && (
                      <div className="absolute left-2 top-8 bottom-0 w-0.5 bg-[#D5E8E4]"></div>
                    )}
                    <div className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 ${
                      stage.status === 'approved' ? 'bg-[#10B981] border-[#10B981]' :
                      stage.status === 'rejected' ? 'bg-[#EF4444] border-[#EF4444]' :
                      'bg-white border-[#D5E8E4]'
                    }`}></div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#052A23]">{stage.stage}</h4>
                      <p className={`text-xs ${getStatusColor(stage.status)} inline-block px-2 py-0.5 rounded mt-1`}>
                        {stage.status.toUpperCase()}
                      </p>
                      {stage.approver_name && (
                        <p className="text-xs text-[#6D8E87] mt-1">By: {stage.approver_name}</p>
                      )}
                      {stage.comments && (
                        <p className="text-xs text-[#3B5A54] mt-1 italic">&ldquo;{stage.comments}&rdquo;</p>
                      )}
                      <p className="text-xs text-[#6D8E87] mt-1">
                        {new Date(stage.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#6D8E87] text-center py-4">No approval history yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationDetail;
